import humps from 'lodash-humps-ts';
import { omit } from 'lodash';
import { snakes } from '@utils/lodash';
import { authAxios } from '@components/Auth';
import { HealthClaim } from '@models/HealthClaim';
import { API_V1_BASE } from '@config';
import { PaymentInfo } from '@models/PaymentInfo';

export interface CreateHealthClaimParams {
  insurancePolicyUserId: number;
  accidentHappenedAt: string;
  symptomsDescription: string;
  diagnosisDescription: string;
  coveredByOtherInsurance: boolean;
  noteOnOtherInsurance?: string;
  roomStay: boolean;
  dischargedAt?: string;
  hospitalLengthOfStay?: number;
  hospitalName?: string;
  medicalDocuments: File[];
  identityDocuments: File[];
  totalClaimedAmount: number;
  mobileNumber: string;
  email?: string;
  paymentInfo: PaymentInfo;
  emailNotifications: boolean;
  acceptTermsAndConditions: boolean;
}

export interface GetHealthClaimParams {
  id: number;
}

export type UpdateHealthClaimParams = GetHealthClaimParams & {
  accidentHappenedAt: string;
  dischargedAt: string;
  hospitalLengthOfStay: number;
  symptomsDescription: string;
  diagnosisDescription: string;
  hospitalName: string;
  diagnosisCodeIds?: number[];
};

export type ApproveHealthClaimParams = GetHealthClaimParams;

export interface UploadHealthClaimDocumentsParams extends GetHealthClaimParams {
  medicalDocuments: File[];
  identityDocuments: File[];
  settlementLetters: File[];
}

export interface UpdateHealthClaimSettlementLetterNotesParams extends GetHealthClaimParams {
  settlementLetterNotes: string | '';
}

export class HealthClaimApi {
  static create = async ({ paymentInfo, ...rest }: CreateHealthClaimParams) => {
    return await authAxios
      .postForm(`${API_V1_BASE}/user/health_claims`, {
        ...omit(snakes(rest), ['medical_documents', 'identity_documents']),
        'medical_documents[]': rest.medicalDocuments,
        'identity_documents[]': rest.identityDocuments,
        'payment_info[payment_method]': paymentInfo.paymentMethod,
        'payment_info[bank_name]': paymentInfo.bankName,
        'payment_info[beneficiary_name]': paymentInfo.beneficiaryName,
        'payment_info[bank_account]': paymentInfo.bankAccount,
      })
      .then((response) => humps(response.data) as HealthClaim);
  };

  static get = async ({ id }: GetHealthClaimParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/user/health_claims/${id}`)
      .then((response) => humps(response.data) as HealthClaim);
  };

  static update = async ({ id, ...params }: UpdateHealthClaimParams) => {
    return await authAxios
      .put(`${API_V1_BASE}/user/health_claims/${id}`, snakes(params))
      .then((response) => humps(response.data) as HealthClaim);
  };

  static uploadDocuments = async ({ id, ...documents }: UploadHealthClaimDocumentsParams) => {
    return await authAxios
      .putForm(`${API_V1_BASE}/user/health_claims/${id}/upload_documents`, {
        ...omit(snakes(documents), ['medical_documents', 'identity_documents', 'settlement_letters']),
        'medical_documents[]': documents.medicalDocuments,
        'identity_documents[]': documents.identityDocuments,
        'settlement_letters[]': documents.settlementLetters
      })
      .then((response) => humps(response.data) as HealthClaim);
  };

  static updateSettlementLetterNotes = async ({ id, settlementLetterNotes }: UpdateHealthClaimSettlementLetterNotesParams) => {
    return await authAxios
      .put(`${API_V1_BASE}/user/health_claims/${id}/settlement_letter_notes`, snakes({ settlementLetterNotes }))
      .then((response) => humps(response.data) as HealthClaim);
  };

  static approve = async ({ id }: ApproveHealthClaimParams) => {
    return await authAxios
      .post(`${API_V1_BASE}/user/health_claims/${id}/approve`)
      .then((response) => humps(response.data) as HealthClaim);
  };
}
